/**
 * Spec Phase 4 Slice 4D — FooterStatusStrip.
 *
 * Bottom-of-editor status strip per brief §4 / handoff:
 *   counts (paragraphs / numerals / examiner flags / empty paras) on
 *   the left, "Section NN · STATUTE" plus Prev/Next chevrons on the
 *   right.
 */
import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useSpecStore } from '@/store/specStore';
import type { SpecSectionRow } from '@/api/client';

interface Props {
  section: SpecSectionRow | null;
}

export default function FooterStatusStrip({ section }: Props): JSX.Element {
  const sections = useSpecStore((s) => s.sections);
  const paragraphsBySection = useSpecStore((s) => s.paragraphsBySection);
  const examinerFlags = useSpecStore((s) => s.examinerFlags);
  const referenceSigns = useSpecStore((s) => s.referenceSigns);
  const featureMappings = useSpecStore((s) => s.featureMappings);
  const setActiveSection = useSpecStore((s) => s.setActiveSection);
  const setDockOpen = useSpecStore((s) => s.setDockOpen);

  const idx = section ? sections.findIndex((s) => s.id === section.id) : -1;
  const prev = idx > 0 ? sections[idx - 1] : null;
  const next =
    idx >= 0 && idx < sections.length - 1 ? sections[idx + 1] : null;

  const paragraphs = section ? paragraphsBySection[section.id] ?? [] : [];
  const paragraphCount = paragraphs.length;
  const numeralCount = referenceSigns.length;
  const flagCount = examinerFlags.filter(
    (f) => f.status === 'open',
  ).length;
  const emptyParaCount = paragraphs.filter(
    (p) => !p.body_text || p.body_text.trim() === '',
  ).length;
  // Phase 8 Slice 8C — feature-mapping anchored ratio.
  const featureTotal = featureMappings.length;
  const featureAnchored = featureMappings.filter(
    (f) => f.anchor_paragraph_id !== null,
  ).length;

  const seq = section ? String(section.sequence_num).padStart(2, '0') : '--';

  return (
    <div
      className="flex items-center justify-between border-t border-zinc-200 bg-white px-6 py-2"
      data-testid="spec-footer-strip"
    >
      <div className="flex items-center gap-4 text-[11px]">
        <span className="text-emerald-700">
          {paragraphCount} paragraph{paragraphCount === 1 ? '' : 's'}
        </span>
        <span className="text-emerald-700">
          {numeralCount} numeral{numeralCount === 1 ? '' : 's'} referenced
        </span>
        {flagCount > 0 && (
          <button
            type="button"
            className="cursor-pointer text-amber-700 hover:underline"
            onClick={() => setDockOpen('examinerView', true)}
            data-testid="spec-footer-flag-count"
            title="Open Examiner View dock"
          >
            {flagCount} examiner flag{flagCount === 1 ? '' : 's'}
          </button>
        )}
        {emptyParaCount > 0 && (
          <span className="text-zinc-500">
            {emptyParaCount} empty para{emptyParaCount === 1 ? '' : 's'}
          </span>
        )}
        {featureTotal > 0 && (
          <button
            type="button"
            className="cursor-pointer font-mono text-zinc-600 hover:underline"
            onClick={() => setDockOpen('featureMap', true)}
            data-testid="spec-footer-feature-count"
            title="Open Feature → Spec Map dock"
          >
            F: {featureAnchored}/{featureTotal} mapped
          </button>
        )}
      </div>
      <div className="flex items-center gap-3 text-[11px] text-zinc-500">
        <span data-testid="spec-footer-locator">
          Section {seq}
          {section?.statute_ref ? ` · ${section.statute_ref}` : ''}
        </span>
        <button
          type="button"
          className="rounded-sm p-1 text-zinc-500 hover:bg-zinc-100 disabled:opacity-30"
          disabled={!prev}
          onClick={() => prev && setActiveSection(prev.id)}
          aria-label="Previous section"
          data-testid="spec-footer-prev"
        >
          <ChevronLeft className="h-3.5 w-3.5" />
        </button>
        <button
          type="button"
          className="rounded-sm p-1 text-zinc-500 hover:bg-zinc-100 disabled:opacity-30"
          disabled={!next}
          onClick={() => next && setActiveSection(next.id)}
          aria-label="Next section"
          data-testid="spec-footer-next"
        >
          <ChevronRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}
